"use strict";
import { svc_fetchDashboard } from "@/services/dashboard";

export const DASHBOARD_WIDGETS = ["summary", "daily", "hourly", "payments", "channels", "items"];

/**
 * Dashboard model: one state entry and one loading flag per widget.
 * Business purpose: the widgets load in parallel and each card shows its own spinner,
 * so the slower item query does not hold the rest of the page.
 */
const Model = {
	namespace: "m_Dashboard",
	state: {
		data: {},
		loading: {},
		errors: {},
		// Latest request per widget; an older answer arriving late is dropped.
		requests: {},
	},
	reducers: {
		reset() {
			// Forget pending request IDs as well: late responses from the previous scope are discarded.
			return { data: {}, loading: {}, errors: {}, requests: {} };
		},
		handleLoading(state, { payload: { widget, requestId } }) {
			return {
				...state,
				loading: { ...state.loading, [widget]: true },
				errors: { ...state.errors, [widget]: false },
				requests: { ...state.requests, [widget]: requestId },
			};
		},
		handleFetch(state, { payload: { widget, requestId, result, error = false } }) {
			if (state.requests[widget] !== requestId)
				return state;
			return {
				...state,
				data: { ...state.data, [widget]: result },
				loading: { ...state.loading, [widget]: false },
				errors: { ...state.errors, [widget]: error },
			};
		},
	},
	effects: {
		/**
		 * Loads one widget.
		 * @param {{widget: string, params: object}} payload Widget name and query (from, to, brandId, outletId).
		 * @returns {Generator} Stores the result or a separate error state when the request failed.
		 * Business purpose: refreshes one card when the period or brand / outlet changes.
		 */
		*fetch({ payload: { widget, params } }, { call, put }) {
			const requestId = `${Date.now()}-${Math.random()}`;
			yield put({ type: "handleLoading", payload: { widget, requestId } });
			let result = null, error = false;
			try {
				const response = yield call(svc_fetchDashboard, widget, params);
				// request.js returns a failed Response, or undefined for a network failure.
				error = !response || !!(response.status && response.headers);
				result = error ? null : response;
			} catch {
				error = true;
			}
			yield put({ type: "handleFetch", payload: { widget, requestId, result, error } });
		},
	},
	subscriptions: {},
};

export default Model;
